import { Box, Tab, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Score,
  TeamColourCount,
  WheelPositionByOrganisation,
  transformTeamWheelByOrganisationStructure,
} from 'api';
import { TextV2, Alert } from '@insights-ltd/design-library/components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import BackButton from 'components/BackButton/BackButton';
import { backButtonStyles } from 'components/layout/BreadcrumbLayout/BreadcrumbLayout';
import * as datadog from 'utils/datadog-error';
import DownloadAll from './DownloadAll';
import {
  BothPercentages,
  getInitialsFromScores,
  getPercentagesFromCounts,
  getPercentagesFromScores,
  getWheelPositionCountsFromScores,
} from './scoreUtils';
import SelectLanguageControl from './SelectLanguageControl';
import SelectTypeControl from './SelectTypeControl';
import { TabTeamEnergyChart, TabTeamNames, TabTeamWheel } from './tabs';
import { WheelState, useWheelContext } from './hooks/useWheelContext';

type TeamWheelType = 'Experience' | 'Team';
type ScoresViewType = 'wheel' | 'names';

type TeamWheelProps = {
  type: TeamWheelType;
  test: boolean;
  rawScores: (Score & { organisationId?: string; organisationName?: string })[];
  counts: TeamColourCount[];
  name: string;
  teamLearnerCount: number;
  purchasedCount: number;
  eventId: string | undefined;
  teamId: string | undefined;
  rawWheelPositions: WheelPositionByOrganisation[];
};

const TeamWheel = ({
  type,
  test,
  rawScores,
  counts,
  name,
  purchasedCount,
  eventId,
  teamId,
  rawWheelPositions,
  teamLearnerCount,
}: TeamWheelProps) => {
  const { t } = useTranslation();
  const [scoresView, setScoresView] = useState<ScoresViewType>('wheel');
  const [downloadErrorFileName, setDownloadErrorFileName] =
    useState<string>('');
  const [
    { scoresGroupingType, isLearnerCountExceeded, activeOrganisations = {} },
    setState,
  ] = useWheelContext();

  const percentages: BothPercentages =
    type === 'Experience'
      ? getPercentagesFromScores(rawScores)
      : getPercentagesFromCounts(counts);

  const processedWheelPositions = transformTeamWheelByOrganisationStructure(
    activeOrganisations,
    rawWheelPositions,
  ).wheelPositions;

  const scoresWithInitials = getInitialsFromScores(rawScores);
  const wheelDisplayScores =
    scoresGroupingType === 'DISTINCT'
      ? scoresWithInitials
      : getWheelPositionCountsFromScores(rawScores);

  const handleReportError = (error: Error, filename: string) => {
    setDownloadErrorFileName(filename);
    datadog.addError(error);
  };

  const resetOrganisationFilter = () => {
    const newValue: Record<string, boolean> = {};

    Object.keys(activeOrganisations ?? {}).forEach((id) => {
      newValue[id] = true;
    });

    setState((prevState: WheelState) => ({
      ...prevState,
      activeOrganisations: newValue,
    }));
  };

  const parentLink =
    type === 'Experience' ? `/experiences/${eventId}` : `/teams/${teamId}`;

  const backTextKey =
    type === 'Experience'
      ? 'ui.event-management.events.add-learners-and-contributors.learner.back-to-experience-overview'
      : 'ui.event-management.teams.wheel.back';

  return (
    <>
      <Grid container spacing={!isLearnerCountExceeded ? 0 : 42} wrap="nowrap">
        <Grid size={!isLearnerCountExceeded ? 7 : 8}>
          <BackButton
            parentLink={parentLink}
            i18nKey={backTextKey}
            sx={backButtonStyles}
          />
          <TextV2 component="h1" variant="h2">
            {t('ui.event-management.events.team-wheel.heading')}
          </TextV2>
          <Box
            sx={(theme) => ({
              mt: theme.spacing(spacingSizeMap.M),
              mb: theme.spacing(spacingSizeMap.L),
            })}
          >
            {isLearnerCountExceeded && (
              <Box sx={{ margin: '2rem 0' }}>
                <Alert severity="warning">
                  <TextV2 sx={{ color: 'inherit', fontWeight: 'inherit' }}>
                    {t(
                      'ui.event-management.events.team-wheel.cannot-download.too-many-learners.warning',
                    )}
                  </TextV2>
                </Alert>
              </Box>
            )}
            <TextV2 component="h2" variant="h4" color="textSecondary">
              {t(
                'ui.event-management.events.download-team-wheel.purchase.count',
                { count: purchasedCount, total: teamLearnerCount },
              )}
            </TextV2>
            {type === 'Team' ? (
              <Typography
                sx={(theme) => ({
                  color: theme.palette.orange.dark,
                  fontWeight: theme.typography.fontWeightBold,
                  marginTop: '0.5rem',
                })}
              >
                {t(
                  'ui.event-management.events.download-team-wheel.discovery_message',
                )}
              </Typography>
            ) : null}
          </Box>
          {downloadErrorFileName && (
            <Box sx={(theme) => ({ py: theme.spacing(spacingSizeMap.M) })}>
              <Alert severity="error">
                <TextV2 sx={{ color: 'inherit', fontWeight: 'inherit' }}>
                  {t('ui.event-management.events.team-wheel.download.error', {
                    fileName: downloadErrorFileName,
                  })}
                </TextV2>
              </Alert>
            </Box>
          )}
        </Grid>
        <Grid size={!isLearnerCountExceeded ? 5 : 1}>
          <DownloadAll
            wheelDisplayScores={wheelDisplayScores}
            initialledScores={scoresWithInitials}
            percentages={percentages}
            reportError={handleReportError}
            name={name}
            disabled={purchasedCount < 1}
            teamId={teamId || ''}
            eventId={eventId}
          />
        </Grid>
      </Grid>
      <TabContext value={scoresView}>
        <Box sx={(theme) => ({ mb: theme.spacing(spacingSizeMap.L) })}>
          <Grid container>
            <Grid size={{ xs: 12, md: 4 }}>
              <SelectLanguageControl />
            </Grid>
            <Grid
              container
              size={{ xs: 12, md: 8 }}
              sx={{
                justifyContent: 'space-around',
              }}
            >
              <Grid size={{ xs: 12, md: 8 }}>
                <SelectTypeControl />
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Grid
          container
          sx={{
            justifyContent: 'space-between',
          }}
        >
          <Grid size={{ xs: 12, md: 'auto' }}>
            <TabList
              onChange={(_, value) => {
                setScoresView(value);
                resetOrganisationFilter();
              }}
              textColor="primary"
              indicatorColor="primary"
              aria-label="team-wheel-tabs"
            >
              <Tab
                label={t('ui.event-management.events.team-wheel.wheel-tab')}
                value="wheel"
              />
              <Tab
                label={t('ui.event-management.events.team-wheel.names-tab')}
                value="names"
              />
              <Tab
                label={t('ui.event-management.events.team-wheel.charts-tab')}
                value="charts"
              />
            </TabList>
          </Grid>
        </Grid>
        <TabPanel
          style={{ padding: 0 }}
          value="wheel"
          aria-label={t(
            'ui.event-management.events.team-wheel.tabs.wheel-view',
          )}
        >
          <TabTeamWheel
            type={type}
            rawScores={rawScores}
            name={name}
            availableCount={purchasedCount}
            reportError={handleReportError}
            wheelPositions={processedWheelPositions}
          />
        </TabPanel>
        <TabPanel
          style={{ padding: 0 }}
          value="names"
          aria-label={t(
            'ui.event-management.events.team-wheel.tabs.names-view',
          )}
        >
          <TabTeamNames
            type={type}
            test={test}
            rawScores={rawScores}
            name={name}
            availableCount={purchasedCount}
            reportError={handleReportError}
          />
        </TabPanel>
        <TabPanel
          style={{ padding: 0 }}
          value="charts"
          aria-label={t(
            'ui.event-management.events.team-wheel.tabs.charts-view',
          )}
        >
          <TabTeamEnergyChart
            percentages={percentages}
            name={name}
            availableCount={purchasedCount}
            reportError={handleReportError}
          />
        </TabPanel>
      </TabContext>
    </>
  );
};

export default TeamWheel;
